import GCPSignedUrlService, { SignedUrlResponse } from "./signed-url-service";

export interface UploadResult {
  success: boolean;
  signedUrlResponse?: SignedUrlResponse;
  error?: string;
  uploadUrl?: string;
}

class GCPUploadService {
  /**
   * Upload a file to GCP using a signed URL
   * @param signedUrl - The signed URL from GCP API
   * @param file - The file to upload
   * @param mimeType - MIME type of the file
   * @returns Promise<boolean> - true if upload successful
   */
  static async uploadFileToGCP(
    signedUrl: string,
    file: File,
    mimeType: string
  ): Promise<boolean> {
    try {
      const response = await fetch(signedUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": mimeType,
        },
      });

      if (!response.ok) {
        throw new Error(
          `Upload failed with status ${response.status}: ${response.statusText}`
        );
      }

      return true;
    } catch (error: any) {
      console.error("Error uploading file to GCP:", error);
      throw new Error(error?.message || "Failed to upload file to GCP");
    }
  }

  /**
   * Generate signed URL and upload file in one operation
   * @param feature - Feature name (e.g., "assignment")
   * @param fileName - File name with path
   * @param file - The file to upload
   * @param fileMimeType - MIME type of the file
   * @returns Promise<UploadResult>
   */
  static async generateSignedUrlAndUpload(
    feature: string,
    fileName: string,
    file: File,
    fileMimeType: string
  ): Promise<UploadResult> {
    try {
      const fileSize = file.size.toString();

      // Generate signed URL
      const signedUrlResponse = await GCPSignedUrlService.generateSingleSignedUrl(
        feature,
        fileName,
        fileMimeType,
        fileSize
      );

      if (signedUrlResponse.status !== "success") {
        return {
          success: false,
          error: `Failed to generate signed URL: ${signedUrlResponse.status}`,
        };
      }

      // Upload file using signed URL
      await this.uploadFileToGCP(
        signedUrlResponse.signed_url,
        file,
        fileMimeType
      );

      return {
        success: true,
        signedUrlResponse,
        uploadUrl: signedUrlResponse.signed_url,
      };
    } catch (error: any) {
      console.error("Error in upload process:", error);
      return {
        success: false,
        error: error?.message || "Unknown error during upload",
      };
    }
  }

  /**
   * Batch upload multiple files
   * @param feature - Feature name
   * @param files - Array of files with metadata
   * @returns Promise<UploadResult[]>
   */
  static async batchUpload(
    feature: string,
    files: Array<{ file: File; fileName: string; mimeType: string }>
  ): Promise<UploadResult[]> {
    const uploadPromises = files.map((fileData) =>
      this.generateSignedUrlAndUpload(
        feature,
        fileData.fileName,
        fileData.file,
        fileData.mimeType
      )
    );

    return Promise.all(uploadPromises);
  }
}

export default GCPUploadService;
